/**
 * 중앙 공정도 (계획서 §13.1, UI_design.pptx).
 *
 * `viewBox` 기반 SVG 라 1920×1080 과 1366×768 에서 같은 배치로 늘어난다.
 * 상태는 색상만으로 구분하지 않고 icon 과 text 를 함께 얹는다 (§13.1).
 *
 * ```text
 * 용접기 → 에폭시 → 건조기
 *    ↑        ↑        ↑
 * 컴프레서          집진기
 * ```
 */

import type { EquipmentSnapshot } from "@/types/api";
import { dryerBadge, productionBadge, runningBadge, withUnit } from "@/lib/format";
import type { StatusBadge } from "@/lib/format";

interface Props {
  equipment: EquipmentSnapshot;
  /** AI 가 제안한 상태 요약. 없으면 "—" 를 보여 준다 (§13.1 [현재상태][AI상태]). */
  aiSummaries: Record<string, string | undefined>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

interface BoxProps {
  id: string;
  x: number;
  y: number;
  title: string;
  badge: StatusBadge;
  lines: string[];
  aiSummary: string | undefined;
  selected: boolean;
  onSelect: (id: string) => void;
}

const BOX_WIDTH = 230;
const BOX_HEIGHT = 132;

function EquipmentBox({
  id,
  x,
  y,
  title,
  badge,
  lines,
  aiSummary,
  selected,
  onSelect,
}: BoxProps): React.JSX.Element {
  return (
    <g
      className={`diagram-box tone-${badge.tone}${selected ? " is-selected" : ""}`}
      transform={`translate(${String(x)}, ${String(y)})`}
      onClick={() => {
        onSelect(id);
      }}
      role="button"
      tabIndex={0}
      aria-label={`${title} ${badge.label}`}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onSelect(id);
      }}
    >
      <rect width={BOX_WIDTH} height={BOX_HEIGHT} rx={10} className="diagram-box-bg" />
      <text x={14} y={26} className="diagram-title">
        {title}
      </text>
      <text x={14} y={50} className="diagram-status">
        {badge.icon} {badge.label}
      </text>
      {lines.slice(0, 2).map((line, index) => (
        <text key={line} x={14} y={72 + index * 18} className="diagram-line">
          {line}
        </text>
      ))}
      <text x={14} y={118} className="diagram-ai">
        AI {aiSummary ? truncate(aiSummary, 26) : "—"}
      </text>
    </g>
  );
}

function truncate(value: string, limit: number): string {
  return value.length <= limit ? value : `${value.slice(0, limit)}…`;
}

export function ProcessDiagram({
  equipment,
  aiSummaries,
  selectedId,
  onSelect,
}: Props): React.JSX.Element {
  const { welder, epoxy, dryer, dust_collector: collector, compressor } = equipment;

  return (
    <svg
      className="process-diagram"
      viewBox="0 0 820 420"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="공정 흐름도"
    >
      {/* 흐름 화살표: 용접기 → 에폭시 → 건조기 */}
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7"
          orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" className="diagram-arrow-head" />
        </marker>
      </defs>

      <line x1={250} y1={86} x2={288} y2={86} className="diagram-arrow" markerEnd="url(#arrow)" />
      <line x1={540} y1={86} x2={578} y2={86} className="diagram-arrow" markerEnd="url(#arrow)" />
      {/* 유틸리티 공급: 컴프레서 → 용접기·에폭시, 집진기 → 건조기 */}
      <line x1={135} y1={268} x2={135} y2={158} className="diagram-arrow" markerEnd="url(#arrow)" />
      <line x1={190} y1={268} x2={400} y2={158} className="diagram-arrow" markerEnd="url(#arrow)" />
      <line x1={690} y1={268} x2={690} y2={158} className="diagram-arrow" markerEnd="url(#arrow)" />

      <EquipmentBox
        id="WELDER"
        x={20}
        y={20}
        title="용접기"
        badge={productionBadge(welder.state)}
        lines={[
          `생산 ${welder.units_completed.toLocaleString("ko-KR")} / ${welder.daily_target_units.toLocaleString("ko-KR")}개`,
          `WIP ${welder.wip.toLocaleString("ko-KR")}개`,
        ]}
        aiSummary={aiSummaries.WELDER}
        selected={selectedId === "WELDER"}
        onSelect={onSelect}
      />
      <EquipmentBox
        id="EPOXY"
        x={295}
        y={20}
        title="에폭시 주입기"
        badge={productionBadge(epoxy.state)}
        lines={[
          `생산 ${epoxy.units_completed.toLocaleString("ko-KR")}개`,
          epoxy.repair_total_seconds
            ? `수리 ${String(Math.floor((epoxy.repair_elapsed_seconds ?? 0) / 60))}/${String(Math.floor(epoxy.repair_total_seconds / 60))}분`
            : `cycle ${epoxy.cycle_seconds}초`,
        ]}
        aiSummary={aiSummaries.EPOXY}
        selected={selectedId === "EPOXY"}
        onSelect={onSelect}
      />
      <EquipmentBox
        id="DRYER"
        x={570}
        y={20}
        title="건조기"
        badge={dryer ? dryerBadge(dryer.mode) : { icon: "■", label: "정지", tone: "idle" }}
        lines={
          dryer
            ? [`온도 ${withUnit(dryer.temperature_c, "℃")}`, `duty ${withUnit(dryer.heater_duty_pct, "%")}`]
            : ["계획 수립 전"]
        }
        aiSummary={aiSummaries.DRYER}
        selected={selectedId === "DRYER"}
        onSelect={onSelect}
      />
      <EquipmentBox
        id="COMPRESSOR"
        x={20}
        y={270}
        title="컴프레서"
        badge={runningBadge(compressor.running)}
        lines={[
          `${withUnit(compressor.target_mpa, "MPa")} · ${withUnit(compressor.hz, "Hz")}`,
          `tank ${withUnit(compressor.tank_pct, "%")}`,
        ]}
        aiSummary={aiSummaries.COMPRESSOR}
        selected={selectedId === "COMPRESSOR"}
        onSelect={onSelect}
      />
      <EquipmentBox
        id="DUST_COLLECTOR"
        x={570}
        y={270}
        title="집진기"
        badge={runningBadge(collector?.running ?? false)}
        lines={
          collector
            ? [withUnit(collector.hz, "Hz"), withUnit(collector.power_kw, "kW")]
            : ["계획 수립 전"]
        }
        aiSummary={aiSummaries.DUST_COLLECTOR}
        selected={selectedId === "DUST_COLLECTOR"}
        onSelect={onSelect}
      />
    </svg>
  );
}
